import type { MenuItemType } from "../../../../types/baseTypes";
import { customSanitize } from "../../../../utils/customSanitize";
import SubMenu from "../SubMenu/SubMenu";
import styles from "./MenItem.module.scss";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { MenuParentType } from "../Menu";
import {
  isChildCurrentPage,
  isMenuItemCurrentPage,
  isMenuItemOrChildCurrentPage,
  isMenuOrChildMenuSelected,
} from "../utils";
import { useAppContext } from "../../../../context/AppContext";
import { useEffect, useRef } from "react";
import type { RefObject } from "react";
import { useLenis } from "lenis/react";
import { optimizeURL } from "@/utils/url";
import type { Post } from "@aleksundshantu/gp-types/client";
import { CustomLink } from "@/components/CustomLink";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG.client";
import { usePathname } from "next/navigation";
import { MegaMenu } from "../MegaMenu/MegaMenu";
import type {
  MegaMenuHighlightType,
  MegaMenuKeyType,
} from "../MegaMenu/MegaMenu";
import type { OptionsNavigationItemType } from "@/types/OptionType";
import type { MenuType } from "@/types/baseTypes";

const HIGHTLIGHT_CURRENT_PAGE = true;
const HIGHLIGHT_CURRENT_PAGES_PARENTS = true;

interface MenuItemProps {
  item: MenuItemType;
  pageData?: Post;
  currentSubmenu: MenuParentType;
  setCurrentSubmenu: (value: MenuParentType) => void;
  parentSubMenu: MenuParentType;
  tabIndex: number;
  itemRef: RefObject<HTMLLIElement>;
  controlParentMenu: (action: "close" | "prev" | "next") => void;
  onFocus?: () => void;
  subMenu: MenuItemType;
  megaMenuKey?: MegaMenuKeyType;
  megaMenuOption?: OptionsNavigationItemType;
  megaMenuHighlights?: MegaMenuHighlightType[];
  topMenu?: MenuType | null;
}

const MenuItem = ({
  item,
  pageData,
  currentSubmenu,
  setCurrentSubmenu,
  parentSubMenu,
  tabIndex,
  itemRef,
  controlParentMenu,
  onFocus,
  subMenu,
  megaMenuKey,
  megaMenuOption,
  megaMenuHighlights = [],
  topMenu = null,
}: MenuItemProps) => {
  const { setOpenMenu } = useAppContext();
  const { getLabel } = useGlobalContext();
  const lenis = useLenis();
  const hasChildren = item.child_items && item.child_items.length > 0;
  const isSimpleLanguage = item.classes?.includes("simple-language");

  const pathname = usePathname();

  const isCurrentPage = HIGHTLIGHT_CURRENT_PAGE
    ? HIGHLIGHT_CURRENT_PAGES_PARENTS
      ? isMenuItemOrChildCurrentPage(item, pageData)
      : isMenuItemCurrentPage(item, pageData)
    : false;

  const { getIcon } = useGlobalContext();

  const isInSubMenu = parentSubMenu !== undefined;
  const hasMegaMenu = !isInSubMenu && !!megaMenuKey && !!megaMenuOption;
  const wasCurrentOnPointerDown = useRef<boolean | null>(null);

  const mouseOverOrFocus = () => {
    if (parentSubMenu) return;
    if (isMenuOrChildMenuSelected(item, currentSubmenu)) return;
    // Switching between two mega menus: open the new one directly instead of
    // resetting to undefined first, which would briefly close the overlay and
    // cause a flicker. Only applies when a mega menu is already open.
    if (hasMegaMenu && currentSubmenu) {
      setCurrentSubmenu(item);
      return;
    }
    setCurrentSubmenu(undefined);
  };

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setCurrentSubmenu(undefined);
        itemRef.current?.querySelector("button")?.focus();
      }
    };

    if (currentSubmenu?.ID === item.ID) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [currentSubmenu, item.ID, setCurrentSubmenu, itemRef]);

  return (
    <li
      role="none"
      className={`${styles["menu-item"]} menu-item ${isCurrentPage ? "is-active" : ""}`}
      ref={itemRef}
    >
      {hasChildren && (
        <>
          <button
            className={`${isInSubMenu ? "navbar-sublink" : "navbar-link"} ${isCurrentPage ? "is-active" : ""} is-flex is-align-items-center is-gap-2 button-reset`}
            onPointerDown={() => {
              wasCurrentOnPointerDown.current = currentSubmenu?.ID === item.ID;
            }}
            onClick={(e) => {
              if (e.screenX === 0 && e.screenY === 0) {
                // https://stackoverflow.com/a/61323763
                // enter pressed - handled by menu onKeyDown
                return;
              }
              if (currentSubmenu?.ID === item.ID) {
                // Pointer focus can select a new mega menu before click fires.
                // Only toggle closed when this item was already open at the
                // start of the pointer interaction.
                if (wasCurrentOnPointerDown.current === false) {
                  wasCurrentOnPointerDown.current = null;
                  return;
                }

                // Repeat click on the open item toggles it closed. Nested items
                // collapse to their parent; top-level items close entirely.
                setCurrentSubmenu(isInSubMenu ? parentSubMenu : undefined);
                wasCurrentOnPointerDown.current = null;
                return;
              }
              setCurrentSubmenu(item);
              wasCurrentOnPointerDown.current = null;
            }}
            onMouseOver={hasMegaMenu ? undefined : mouseOverOrFocus}
            onFocus={() => {
              mouseOverOrFocus();
              onFocus?.();
            }}
            aria-expanded={isMenuOrChildMenuSelected(item, currentSubmenu)}
            role="menuitem"
            aria-haspopup={"true"}
            title={
              isChildCurrentPage(item, pageData)
                ? getLabel(
                    "nav_item_includes_current_page",
                    "Contains current page link",
                  )
                : ""
            }
            tabIndex={tabIndex}
          >
            {!isInSubMenu && (
              <CustomSVG
                width={12}
                height={11}
                className={styles["menu-item-chevron"]}
                src={getIcon("chevron-down")}
                wrapper={"span"}
                aria-hidden="true"
              />
            )}
            <span
              className={`${styles["menu-item-link"]}`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(item.title),
              }}
            />
            <CustomSVG
              width={16}
              height={16}
              className="is-hidden-after-navbar-breakpoint navbar-sublink"
              src={getIcon("altArrowRight")}
              wrapper={"span"}
              aria-hidden="true"
            />
          </button>

          <SubMenu
            item={item}
            pageData={pageData}
            currentSubmenu={currentSubmenu}
            setCurrentSubmenu={setCurrentSubmenu}
            parentSubMenu={parentSubMenu}
            subMenu={subMenu}
            parentMenuItem={item}
            controlParentMenu={controlParentMenu}
            className={hasMegaMenu ? styles["legacy-submenu"] : ""}
            culinary={megaMenuKey === "kulinarik"}
            megaMenuOption={megaMenuOption}
            megaMenuHighlights={megaMenuHighlights}
            topMenu={topMenu}
          />
          {hasMegaMenu && (
            <MegaMenu
              item={item}
              isOpen={currentSubmenu?.ID === item.ID}
              option={megaMenuOption}
              highlights={megaMenuHighlights}
              topMenu={topMenu}
              isCulinary={megaMenuKey === "kulinarik"}
              onClose={() => {
                setCurrentSubmenu(undefined);
                itemRef.current?.querySelector("button")?.focus();
              }}
              onNavigate={() => {
                setOpenMenu(false);
                setCurrentSubmenu(undefined);
              }}
            />
          )}
        </>
      )}

      {!hasChildren && (
        <CustomLink
          className={`${isInSubMenu ? "navbar-sublink" : "navbar-link"} ${styles["menu-item-link"]} ${isCurrentPage ? "is-active" : ""} ${isSimpleLanguage ? styles["has-simple-language-icon"] : ""} is-flex is-align-items-center is-gap-2`}
          href={item.url}
          target={"_self"}
          aria-label={item.title}
          role="menuitem"
          onClick={() => {
            // if nav item has anchor (#) which is on the current page use lenis to scroll to anchor for animation and otherwise it won't work if lenis is currently animating page scroll
            // https://aleksundshantu.slack.com/archives/C08166QC5B2/p1734104421062139
            const url = optimizeURL(item.url);
            if (url && lenis) {
              const urlAnchor = url.split("#")[1];
              const urlWithoutAnchor = url.split("?")[0];
              const element = document.getElementById(urlAnchor);
              const nav = document.querySelector("nav.navbar") as
                | HTMLElement
                | undefined;
              if (pathname === urlWithoutAnchor && element) {
                lenis.scrollTo(element.offsetTop - (nav?.offsetHeight ?? 0));
              }
            }

            setOpenMenu(false);
            setOpenMenu(false);
            setCurrentSubmenu(undefined);
          }}
          {...(isCurrentPage && { "aria-current": "page" })}
          tabIndex={tabIndex}
          onFocus={onFocus}
          disablePrefetch // disable prefetch for menu items https://trello.com/c/g5k075FS
        >
          <div className={styles["skel-ico"]} />
          {item.title}
          {isSimpleLanguage && (
            <CustomSVG
              width={20}
              height={20}
              className={styles["menu-item-icon"]}
              src={getIcon("simpleLanguage")}
              wrapper={"span"}
              aria-hidden="true"
            />
          )}
        </CustomLink>
      )}
    </li>
  );
};

export default MenuItem;
